import { parseBrandBackground } from '@vedalib/editor/lib/brand'
import cn from 'classnames'
import React, { useMemo, useRef } from 'react'

import { useGetBgImageStyles } from 'components/Course/useGetBgImageStyles'
import NoBlock from 'components/Editor/Canvas/BlockRender/NoBlock'
import { AbsolutePortalProvider } from 'components/Editor/components/AbsolutePortal'
import BlockRenderContainer from 'components/Editor/cource/layout/Block/BlockContainer'
import Scrollbar from 'components/Editor/cource/renderEntry/CourseSection/Scrollbar'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { useBrandTheme } from 'services/Branding/hooks'
import ScrollService from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { courseStart } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getBlocks,
  getDeviceMode,
  getEditorMode,
  getPreviewMode,
} from 'services/Store/Project/selectors'
import type { IBlockMode, Section } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { useBlockViewed } from 'services/lms/hooks'

import * as s from './CourseCover.module.scss'

interface ICourseCoverProps {
  section: Section
  mode: IBlockMode
}

const CONFIG = {
  description: (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Icon name='builderTooltip' size={KitSize.S} />
      <span>{t('entity.section.coverNotChosen')}</span>
    </div>
  ),
  buttonLabel: t('entity.section.nextSection'),
}

const CourseCover: React.FC<ICourseCoverProps> = ({ section: cover }) => {
  const ref = useRef<HTMLDivElement>(null)
  const theme = useBrandTheme()
  const dispatch = useProjectDispatch()
  const previewMode = useProjectContext(getPreviewMode)
  const editorMode = useProjectContext(getEditorMode)
  const deviceMode = useProjectContext(getDeviceMode)
  const blocks = useProjectContext(getBlocks, cover.id)
  const currentBlock = blocks[0]
  const emptyElementsOrNotBlock =
    Boolean(!currentBlock) || Boolean(!Object.keys(currentBlock?.elements || {}).length)
  const onCourseStart = () => {
    dispatch(courseStart())
  }
  const mode = useMemo(
    () => ({ deviceMode, previewMode, editorMode }),
    [deviceMode, previewMode, editorMode],
  )
  const sectionBackground = parseBrandBackground(cover?.style?.background || theme?.background)
  const imgStyles = useGetBgImageStyles(sectionBackground.overlay)

  useBlockViewed({ blockId: currentBlock?.uuid, mode, ref })

  return (
    <Scrollbar ref={ScrollService.createContainerSetter(ScrollContainerEnum.canvas)}>
      <AbsolutePortalProvider viewport={ref}>
        <main
          className={cn(s.root, { [s.emptyBlock]: emptyElementsOrNotBlock })}
          ref={ref}
          style={imgStyles}
          tabIndex={-1}
        >
          {!emptyElementsOrNotBlock &&
            blocks.map((block, index) => (
              <React.Fragment key={block.uuid}>
                <BlockRenderContainer block={block} index={index} mode={mode} />
              </React.Fragment>
            ))}
          {emptyElementsOrNotBlock && <NoBlock config={CONFIG} onClick={onCourseStart} />}
        </main>
      </AbsolutePortalProvider>
    </Scrollbar>
  )
}

export default CourseCover
